/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as GdprDuck from 'core/store/ducks/gdpr';
import { Ducks, UpdatablePreference } from '@wit/vtv-x-foundation-lib';
import translate from '../../../services/i18n/translate';
import { Spinner } from '../../components';
import { Selectors as StartUpSelectors } from '../../../store/ducks/startUp';

const ConsentPage: React.FunctionComponent = () => {
  const consentData = useSelector(Ducks.Faqs.Selectors.getGdprData);

  const pageData = {
    title: translate['gdpr.consent.title'],
    text: consentData?.documents.consent?.text || translate['gdpr.consent.description'],
    ctaText: translate['gdpr.status.agree'],
  };

  const gdprData = useSelector(Ducks.Faqs.Selectors.getGdprData);
  const hasAgreedToConsent = useSelector(GdprDuck.Selectors.hasAgreedToConsent);
  const isProcessing = useSelector(GdprDuck.Selectors.isProcessing);
  const isProcessed = useSelector(GdprDuck.Selectors.isProcessed);
  const isUserAuthenticated = useSelector(Ducks.Credentials.Selectors.isAuthenticated);
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);
  const isAfterInitializingCredentials = useSelector(StartUpSelectors.isAfterInitializingCredentials);
  const isStartUpSuccess = useSelector(StartUpSelectors.isStartUpSuccess);
  const dispatch = useDispatch();

  function consentAgreePressed() {
    if (isUserAuthenticated && !isProcessing) {
      const gdprParams = {
        preference: UpdatablePreference.GDPR_CONSENT,
        preferenceValue: {
          accepted: !hasAgreedToConsent,
          version: gdprData?.documents.consent?.version,
        },
      };
      dispatch(GdprDuck.ActionCreators.updatePreference(gdprParams.preference, gdprParams.preferenceValue));
    }
  }

  return (
    <div id="ConsentPage" className="component-inner gdpr">
      <h2 id="ConsentPage_title">{pageData.title}</h2>
      <div className="gdpr-container">
        <div className="left-info">
          {defaultUser && isUserAuthenticated && isStartUpSuccess && (
            <>
              <div id="ConsentPage_status" className="status">
                <span>
                  {hasAgreedToConsent ? translate['gdpr.status.agreed'] : translate['gdpr.status.not.agreed']}
                </span>
                {hasAgreedToConsent && <span className="status-img" />}
              </div>
              <p id="ConsentPage_description" className="description">
                {translate['gdpr.consent.description']}
              </p>
              <div className="btn-group actions update-status-container">
                <a
                  id="ConsentPage_button___agree"
                  onClick={() => consentAgreePressed()}
                  className={`btn gradient-btn ng-scope ${isProcessing && !isProcessed ? 'disabled' : ''}`}
                >
                  <span id="ConsentPage_updateStatus">
                    {hasAgreedToConsent ? translate['gdpr.status.disagree'] : translate['gdpr.status.agree']}
                  </span>
                </a>
                {isProcessing && !isProcessed && <Spinner isActive isEmbedded />}
              </div>
            </>
          )}
        </div>
        <div className="right-info">
          {isAfterInitializingCredentials && pageData?.text && (
            <div className="text" id="ConsentPage_text">
              <span>{pageData?.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentPage;
